import { api } from "../http";
import type { DocumentUploadResponse } from "../types";
import { createOfflineAttachment } from "../../offline/attachments";
import { isRuntimeOfflineEnabled } from "../../offline/runtime";

export const MAX_DOCUMENT_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024;

const buildOfflineResponse = async (file: File): Promise<DocumentUploadResponse> => {
  const attachment = await createOfflineAttachment(file);
  return {
    id: attachment.id,
    url: `offline://attachment/${attachment.id}`,
    fileName: attachment.fileName,
    contentType: attachment.contentType,
    size: attachment.size,
    offlineAttachmentId: attachment.id,
    offline: true,
  };
};

const isNavigatorOnline = () => (typeof navigator === "undefined" ? true : navigator.onLine);

export const documentUploadApi = {
  async upload(file: File) {
    if (typeof file.size === "number" && file.size > MAX_DOCUMENT_UPLOAD_SIZE_BYTES) {
      throw new Error("File must be 20 MB or smaller.");
    }

    const runtimeOffline = isRuntimeOfflineEnabled();
    if (!isNavigatorOnline() || runtimeOffline) {
      return buildOfflineResponse(file);
    }

    const form = new FormData();
    form.append("file", file);
    try {
      return await api.post<DocumentUploadResponse, FormData>("/api/shared/document-upload", form);
    } catch (error) {
      if (runtimeOffline) {
        return buildOfflineResponse(file);
      }
      const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
      if (error instanceof TypeError || /failed to fetch/i.test(message)) {
        return buildOfflineResponse(file);
      }
      throw error;
    }
  },
};

export type { DocumentUploadResponse };
